'use client';

import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { jobQueueService } from '@/shared/api/services/job-queue.service';
import { useJobQueueStore, type Job } from '../model/job-queue.store';
import { useUser } from '@/app/model/stores';
import { jobQueueLogger } from '@/shared/lib/logger';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { RenderStatus } from '@/shared/constants/status';

/**
 * `dataUpdatedAt` of the last REST queue snapshot written into the store, per
 * query `limit`. Module scope on purpose: it must outlive the component, since
 * the replay it guards against happens on remount.
 */
const lastAppliedSnapshotAt = new Map<number, number>();

/**
 * Hook to fetch and manage job queue (renders, PDF albums, upscales)
 *
 * NOTE: Multiple specialized hooks exist below (useActiveUpscaleForRender, etc.)
 * FUTURE OPTIMIZATION: Consider consolidating into single hook with filters:
 *
 * Example future API:
 * ```typescript
 * useJobQueue({
 *   filter: { jobType: 'upscale', originalRenderId: 'xxx', status: PROCESSING },
 *   mode: 'single' | 'multiple'
 * })
 * ```
 *
 * Current approach (multiple hooks) is acceptable for now.
 */
export function useJobQueue(limit: number = 5) {
  const user = useUser();
  const replaceJobQueue = useJobQueueStore((state) => state.replaceJobQueue);
  const setLoadingQueue = useJobQueueStore((state) => state.setLoadingQueue);
  const jobQueue = useJobQueueStore((state) => state.jobQueue);
  const getProcessingJobs = useJobQueueStore(
    (state) => state.getProcessingJobs
  );

  // Get processing jobs
  const processingJobs = useMemo(
    () => getProcessingJobs(),
    [getProcessingJobs]
  );

  // Memoize processing count to avoid unnecessary recalculations
  const processingCount = useMemo(
    () => processingJobs.length,
    [processingJobs]
  );

  // Fetch queue from API - WebSocket will handle updates
  const { data, dataUpdatedAt, isLoading, error, refetch } = useQuery({
    queryKey: cacheKeys.jobQueue.list(limit),
    queryFn: async () => {
      // jobQueueLogger.debug(`useJobQueue: Fetching queue with limit ${limit}`);
      const result = await jobQueueService.getQueue(limit);
      // jobQueueLogger.info(
      //   `useJobQueue: Fetched ${result.length} jobs from API`
      // );
      return result;
    },
    enabled: !!user,
    // Disabled polling - using WebSocket for real-time updates
    refetchInterval: false,
    staleTime: 10000, // Consider data stale after 10 seconds
  });

  // Push each REST snapshot into the store exactly once. The query keeps its
  // data for gcTime (30 min), so a remount (e.g. ConceptEditor re-entered)
  // hands back the SAME old snapshot; replaying it overwrote everything WS had
  // applied since, and renders that had finished came back as "processing".
  // Only a snapshot newer than the last one applied may replace the store; a
  // stale cache still refetches on mount and lands here as a newer snapshot.
  useEffect(() => {
    if (data) {
      const lastApplied = lastAppliedSnapshotAt.get(limit) ?? 0;
      if (dataUpdatedAt <= lastApplied) {
        return;
      }
      lastAppliedSnapshotAt.set(limit, dataUpdatedAt);
      replaceJobQueue(data);
    } else if (data === undefined && !isLoading && !error) {
      jobQueueLogger.debug('useJobQueue: No data received from API');
    }
  }, [data, dataUpdatedAt, limit, replaceJobQueue, isLoading, error]);

  // Update loading state
  useEffect(() => {
    setLoadingQueue(isLoading);
  }, [isLoading, setLoadingQueue]);

  // Force refetch on mount if queue is empty and we have a user
  useEffect(() => {
    if (user && jobQueue.length === 0 && !isLoading && !data) {
      jobQueueLogger.info(
        'useJobQueue: Queue empty on mount, triggering initial fetch'
      );
      refetch();
    }
  }, [user?.id]); // Only run when user changes, other deps checked inside condition

  return {
    jobQueue,
    processingJobs,
    processingCount,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Hook to get active render for a specific concept angle
 * Excludes upscale renders
 * 2-tier matching: first active (pending/processing), then fallback to failed
 */
export function useActiveJobForConceptAngle(
  conceptAngleId: string | undefined
) {
  return useJobQueueStore((state) => {
    if (!conceptAngleId) return undefined;

    // Tier 1: active job (pending/processing)
    const activeJob = state.jobQueue.find(
      (r) =>
        r.conceptAngleId === conceptAngleId &&
        r.jobType !== 'upscale' &&
        (r.status === RenderStatus.PENDING ||
          r.status === RenderStatus.PROCESSING)
    );
    if (activeJob) return activeJob;

    // Tier 2: failed job (so UI shows failed overlay)
    return state.jobQueue.find(
      (r) =>
        r.conceptAngleId === conceptAngleId &&
        r.jobType !== 'upscale' &&
        r.status === RenderStatus.FAILED
    );
  });
}

/**
 * Hook to get active upscale for a specific render (by original render ID)
 * Returns UpscaleJob with type safety
 */
export function useActiveUpscaleForRender(renderId: string | undefined) {
  return useJobQueueStore((state) => {
    if (!renderId) return undefined;

    // Use Store's typed selector which returns UpscaleJob
    return state.getActiveUpscaleForRender(renderId);
  });
}

/**
 * Hook to get active upscales for multiple renders at once
 * Returns a stable reference using useMemo to prevent infinite loops.
 *
 * Memoizes based on Map contents (size, IDs, statuses) instead of reference.
 */
export function useActiveUpscalesForRenders(
  renderIds: string[]
): Map<string, Job> {
  // Get raw data from store
  const getActiveUpscalesForRenders = useJobQueueStore(
    (state) => state.getActiveUpscalesForRenders
  );
  const jobQueue = useJobQueueStore((state) => state.jobQueue);

  // Memoize result based on actual Map contents
  return useMemo(() => {
    return getActiveUpscalesForRenders(renderIds);
  }, [getActiveUpscalesForRenders, renderIds, jobQueue]); // Re-compute when queue changes
}

/**
 * Hook to get active job for a concept — any generation (render or album).
 * Used by ConceptCard on project page where the card represents the entire concept,
 * so it must show activity for ANY generation (render, album), excluding upscale.
 * Matches Android jobQueue.find and Apple anyOverlayJob(forConceptId:).
 * 2-tier matching: first active (pending/processing), then fallback to failed.
 */
export function useActiveJobForConcept(conceptId: string | undefined) {
  return useJobQueueStore((state) => {
    if (!conceptId) return undefined;

    // Tier 1: active job (pending/processing) — any type except upscale
    const activeJob = state.jobQueue.find(
      (r) =>
        r.conceptId === conceptId &&
        r.jobType !== 'upscale' &&
        (r.status === RenderStatus.PENDING ||
          r.status === RenderStatus.PROCESSING)
    );
    if (activeJob) return activeJob;

    // Tier 2: failed job (so UI shows failed overlay)
    return state.jobQueue.find(
      (r) =>
        r.conceptId === conceptId &&
        r.jobType !== 'upscale' &&
        r.status === RenderStatus.FAILED
    );
  });
}

/**
 * Hook: is a just-completed render for this angle still being prefetched?
 * True between the `completed` WS event and the image being decoded into the
 * browser cache — the window during which the progress overlay must stay up
 * even though the job may already be gone from the queue. Mirrors
 * Apple/Android `isPreloadingRender`.
 */
export function useIsAnglePreloadingRender(
  conceptAngleId: string | undefined
): boolean {
  return useJobQueueStore((state) => {
    if (!conceptAngleId) return false;
    return Object.values(state.preloadingRenders).includes(conceptAngleId);
  });
}

/**
 * Hook to get the latest render for tracking completion
 */
export function useLatestRenderForConceptAngle(
  conceptAngleId: string | undefined
) {
  return useJobQueueStore((state) =>
    conceptAngleId
      ? state.jobQueue.find((r) => r.conceptAngleId === conceptAngleId)
      : undefined
  );
}

/**
 * Hook to get the most recent completed Job (render or upscale) for an angle.
 * Reads only from the in-tab job queue — server Render entities live in
 * TanStack Query (`cacheKeys.renders.list(conceptId)`) and should be read via
 * `useAngleRendersData` / `useRendersForConcept`.
 */
export function useCompletedRenderFromStore(
  conceptAngleId: string | undefined
) {
  // Read raw state to avoid infinite loop from nested selector calls
  const jobQueue = useJobQueueStore((state) => state.jobQueue);

  return useMemo(() => {
    if (!conceptAngleId) return undefined;

    return useJobQueueStore
      .getState()
      .getCompletedJobForConceptAngle(conceptAngleId);
  }, [conceptAngleId, jobQueue]);
}

/**
 * Hook to get completed renders for multiple derivative angles at once
 * Returns a stable reference using useMemo to prevent infinite loops.
 *
 * Memoizes based on Map contents (size, IDs, statuses) instead of reference.
 */
export function useCompletedRendersForAngles(
  angleIds: string[]
): Map<string, Job> {
  // Get raw data from store
  const getCompletedJobsForAngles = useJobQueueStore(
    (state) => state.getCompletedJobsForAngles
  );
  const jobQueue = useJobQueueStore((state) => state.jobQueue);

  // Memoize result based on actual Map contents
  return useMemo(() => {
    return getCompletedJobsForAngles(angleIds);
  }, [getCompletedJobsForAngles, angleIds, jobQueue]); // Re-compute when queue changes
}
